import { CSSProperties, useContext, useState } from "react";
import { KeyboardArrowDown } from "@material-ui/icons";
import QuestionMarkIcon from "assets/images/question-mark.svg";
import Loader from 'elements/Loader';
import DaysSelector from "components/shareable/DaysSelector";
import dayjs from "dayjs";
import useBrand from 'Hooks/useBrand';
import { UpgradeModalContext } from "Hooks/UpgradeModalContext";
import millify from "millify";
import DatePicker from "react-datepicker";
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Text, Tooltip, XAxis, YAxis } from 'recharts';
import "styles/react-datepicker.css";
import { SymphonyTooltip } from "modules/Sty";
import { GraphTooltip, GrowthIndicatorMessage } from "./components/Graphs";
import { CustomDateSelector } from "./components/LargeGraphPopup";
import { MainChartBackground } from "./styles";
import { PlanType } from "modules/Const";
import { hasAnnotations } from "modules/Utils";
import SymphonyDefaultAnnotations from "components/shareable/SymphonyDefaultAnnotations";
import SymphonyDefaultAnnotationsModal from "components/shareable/SymphonyDefaultAnnotationsModal";
import PopoverCampaignAnnotations from "components/shareable/PopoverCampaignAnnotations";
import LegendCampaignAnnotations from "components/shareable/LegendCampaignAnnotations";
import MenuProOption from "components/shareable/MenuProOption";
import { useDashboard } from "Hooks/useDashboard";
import Colors from "modules/Colors";

const OverallDashboard = () => {
    const { handleOpenModal: handleOpenUpgradeToProModal } = useContext(UpgradeModalContext);
    const { currentBrand, isFreeBrand, isProBrand, isLiteBrand } = useBrand();
    const {
        isLoadingNewDate,
        selectedDate,
        setSelectedDate,
        timeRange,
        setTimeRange,
        overallGraphData
    } = useDashboard();

    const [openDaysSelector, setOpenDaysSelector] = useState(false)
    const [showGridModal, setShowGridModal] = useState(false)
    const [selectedCampaign, setSelectedCampaign] = useState<any | null>(null)
    const [selectedIgPost, setSelectedIgPost] = useState<any | null>(null)
    const [selectedSpotifyRelease, setSelectedSpotifyRelease] = useState<any | null>(null)
    const [showAnnotations, setShowAnnotations] = useState({})
    const [itemsArray, setItemsArray] = useState<any[]>([]);

    // Get overall graph data
    const overallFanbaseGraphData = overallGraphData;
    const overallChartHasData = overallFanbaseGraphData && overallFanbaseGraphData.chartData && overallFanbaseGraphData.chartData.length > 0;
    const fanbaseSize = overallChartHasData ? overallFanbaseGraphData.chartData[overallFanbaseGraphData.chartData.length - 1].value : null;

    const handleOpenDaysSelector = () => setOpenDaysSelector(true);
    const handleCloseDaysSelector = () => setOpenDaysSelector(false);

    const handleChangeDaysSelector = (
        event: React.ChangeEvent<{
            name?: string;
            value: unknown;
        }>
    ) => {
        const value = event.target.value as string;
        const isFreeValidDateOptions = value === "last-7-days"
        const isLiteValidDateOptions = value === "last-7-days" || value === "last-30-days"
        const isProValidDateOptions = value === "last-7-days" || value === "last-30-days" || value === "last-3-months" || value === 'custom';

        if (
            (isFreeBrand && isFreeValidDateOptions) ||
            (isLiteBrand && isLiteValidDateOptions) ||
            (isProBrand && isProValidDateOptions)
        ) {
            setTimeRange(value);
        } else {
            handleOpenUpgradeToProModal({
                upgradeCallToAction: "Upgrade to Symphony Pro to unlock unlimited Dashboard data.",
                highlightedPlan: PlanType.PRO,
                source: "Dashboard - Overall Fanbase Date Picker"
            });
        }

        setShowGridModal(false)
        initSelectedItem()
    };

    const initSelectedItem = () => {
        setSelectedCampaign(null)
        setSelectedIgPost(null)
        setSelectedSpotifyRelease(null)
    }


    const renderDateSelectorButtons = () => {
        return (
            <div className="dateSelectorButtonsContainer" style={sty.dateSelectorButtonsContainer}>
                {renderOptionsCampaignComponent()}
                <DaysSelector
                    className="overallDashboardDaysSelector"
                    options={[
                        {
                            id: "7-days-option",
                            component: <p>Last 7 Days</p>,
                            value: "last-7-days",
                        },
                        {
                            id: "30-days-option",
                            component: (
                                <MenuProOption
                                    mainContainerClassName="flex items-center gap-4 w-full"
                                    labelClassName={!isProBrand ? "w-7/12" : ""}
                                    label="Last 30 days"
                                />
                            ),
                            value: "last-30-days",
                        },
                        {
                            id: "3-months-option",
                            component: (
                                <MenuProOption
                                    mainContainerClassName="flex items-center gap-4 w-full"
                                    labelClassName={!isProBrand ? "w-7/12" : ""}
                                    label="Last 3 Months"
                                />
                            ),
                            value: "last-3-months",
                        },
                        {
                            id: "custom-option",
                            component: (
                                <MenuProOption
                                    mainContainerClassName="flex items-center gap-4 w-full"
                                    labelClassName={!isProBrand ? "w-7/12" : ""}
                                    label="Custom"
                                />
                            ),
                            value: "custom",
                        }
                    ]}
                    selectProps={{
                        open: openDaysSelector,
                        className: "h-11",
                        variant: "outlined",
                        value: timeRange,
                        IconComponent: () => (
                            <div
                                className="dropdownArrowDown mr-2"
                                onClick={handleOpenDaysSelector}
                            >
                                <KeyboardArrowDown viewBox="0 -2 26 26" />
                            </div>
                        ),
                        onOpen: handleOpenDaysSelector,
                        onClose: handleCloseDaysSelector,
                        onChange: handleChangeDaysSelector,
                    }}
                />
            </div>
        )
    }

    const renderCustomDatesPicker = () => {
        if (timeRange !== 'custom') {
            return null
        }

        return (
            <div className="customDatePickerContainer mt-3 md:mt-0 flex items-center justify-center">
                <div className="mr-1">From:</div>
                <DatePicker
                    disabled={!isProBrand}
                    filterDate={(date: Date) => {
                        return dayjs(date) < dayjs(selectedDate.endDate).subtract(1, 'day');
                    }}
                    selected={dayjs(selectedDate.startDate).toDate()}
                    onChange={(date) => {
                        setSelectedDate({
                            startDate: dayjs(date).format("YYYY-MM-DD"),
                            endDate: selectedDate.endDate
                        })
                    }}
                    customInput={<CustomDateSelector />}
                />
                <p className="mr-1">To:</p>
                <DatePicker
                    disabled={!isProBrand}
                    minDate={dayjs(selectedDate.startDate).add(1, 'day').toDate()}
                    maxDate={dayjs().toDate()}
                    selected={dayjs(selectedDate.endDate).toDate()}
                    onChange={(date) => {
                        setSelectedDate({
                            endDate: dayjs(date).format("YYYY-MM-DD"),
                            startDate: selectedDate.startDate
                        })
                    }}
                    customInput={<CustomDateSelector />}
                />
            </div>
        )
    }

    const getOverallGraphTooltipText = () => {
        return `This graph shows the total size of your fanbase across your tracked social & streaming platforms.`;
    }

    const renderOptionsCampaignComponent = (style?: CSSProperties) => {
        return (
            <PopoverCampaignAnnotations
                className="switchOverallDashboard"
                style={{ marginLeft: 'auto', ...style }}
                showAnnotations={showAnnotations}
                setShowAnnotations={setShowAnnotations}
                upgradeSource="Overall Dashboard - Fanbase Growth - Annotations"
            />
        )
    }

    return (
        <div className="mx-auto relative overallDashboardComponent">
            {overallChartHasData ?
                <MainChartBackground className="yourFanbaseChartContainer">
                    <SymphonyDefaultAnnotationsModal
                        itemsArray={itemsArray}
                        showGridModal={showGridModal}
                        setShowGridModal={setShowGridModal}
                        selectedCampaign={selectedCampaign}
                        selectedIgPost={selectedIgPost}
                        selectedSpotifyRelease={selectedSpotifyRelease}
                        setSelectedCampaign={setSelectedCampaign}
                        setSelectedIgPost={setSelectedIgPost}
                        setSelectedSpotifyRelease={setSelectedSpotifyRelease}
                    />
                    <div className="mx-4 flex flex-row justify-between">
                        <div className="">
                            <div className=" flex items-center mb-2">
                                <p className="text-lg md:text-xl yourFanbaseText">
                                    Your Fanbase
                                </p>
                                <SymphonyTooltip className=" ml-2 w-6 h-6" title={getOverallGraphTooltipText()} arrow>
                                    <img
                                        className="w-6 h-6"
                                        alt="question-mark-icon.svg"
                                        src={QuestionMarkIcon}
                                    />
                                </SymphonyTooltip>
                            </div>
                            {fanbaseSize &&
                                <div className="fanbaseSizeContainer flex items-center mb-4">
                                    <div className="flex flex-row align-items justify-center gap-2">
                                        <div
                                            className="fanbaseSizeText text-4xl md:text-4xl 2xl:text-5xl font-semibold"
                                        >
                                            {millify(fanbaseSize)}
                                        </div>
                                        {window.innerWidth < 768 && isLoadingNewDate &&
                                            <div className="loadingIndicatorContainer mr-3 flex items-center justify-center">
                                                <Loader height={18} width={18} />
                                            </div>
                                        }
                                    </div>
                                    {window.innerWidth >= 768 && overallChartHasData &&
                                        <GrowthIndicatorMessage
                                            percentage={overallFanbaseGraphData && overallFanbaseGraphData.percentage ? overallFanbaseGraphData.percentage : null}
                                        />
                                    }
                                </div>
                            }
                        </div>
                        {window.innerWidth < 768 && overallChartHasData &&
                            <GrowthIndicatorMessage
                                percentage={overallFanbaseGraphData.percentage}
                            />
                        }
                        {window.innerWidth >= 768 &&
                            <div className="dateSelectorContainer flex-row flex items-center" style={{ marginLeft: 10 }}>
                                {isLoadingNewDate &&
                                    <div className="mr-3">
                                        <Loader height={18} width={18} />
                                    </div>
                                }
                                {renderCustomDatesPicker()}
                                {renderDateSelectorButtons()}
                            </div>
                        }
                    </div>

                    {hasAnnotations(showAnnotations) && <LegendCampaignAnnotations />}

                    <ResponsiveContainer className="mx-auto" width="100%" height={window.innerWidth < 576 ? 175 : window.innerHeight * 0.3} minHeight="100px">
                        <AreaChart
                            width={500}
                            height={300}
                            data={overallFanbaseGraphData.chartData}
                            margin={{
                                top: 15,
                                right: 0,
                                left: window.innerWidth >= 768 ? 20 : 0,
                                bottom: 5,
                            }}
                        >
                            <CartesianGrid
                                stroke="#8800FF29"
                                strokeWidth={1.5}
                                strokeDasharray="8 8"
                            />
                            <XAxis
                                dy={12}
                                axisLine={false}
                                dataKey="date"
                                tickLine={false}
                                minTickGap={window.innerWidth < 768 ? 20 : 30}
                                tickFormatter={(value: any, index: number) => {
                                    return dayjs(value).format("MM/DD")
                                }}
                            />
                            <YAxis
                                mirror={window.innerWidth < 768}
                                tick={(e) => {
                                    const { payload: { value } } = e;
                                    const color = value === "yourdata" ? "red" : "#666";
                                    e["fill"] = color;
                                    if (window.innerWidth >= 768) {
                                        return (
                                            <Text
                                                {...e}
                                                style={{
                                                    zIndex: 99
                                                }}>
                                                {millify(value, {
                                                    precision: 2
                                                })}
                                            </Text>
                                        )
                                    } else {
                                        return (
                                            <>
                                                <rect></rect>
                                                <defs>
                                                    <filter id="rounded-corners-overall" x="-5%" width="110%" y="0%" height="100%">
                                                        <feFlood floodColor="#fff" />
                                                        <feComponentTransfer>
                                                            <feFuncA type="table" tableValues="1 1 1 1" />
                                                        </feComponentTransfer>
                                                        <feComponentTransfer>
                                                            <feFuncA type="table" tableValues="0 1 1 1 1 1 1 1" />
                                                        </feComponentTransfer>
                                                        <feComposite operator="over" in="SourceGraphic" />
                                                    </filter>
                                                </defs>
                                                <Text
                                                    filter="url(#rounded-corners-overall)"
                                                    {...e}
                                                    style={{
                                                        transform: "translateX(-4px)",
                                                        zIndex: 99
                                                    }}>
                                                    {millify(value, {
                                                        precision: 2
                                                    })}
                                                </Text>
                                            </>
                                        )
                                    }
                                }}
                                tickCount={window.innerWidth < 768 ? 3 : 4}
                                axisLine={false}
                                minTickGap={window.innerWidth < 768 ? 30 : 20}
                                tickLine={false}
                                allowDecimals={false}
                                domain={['auto', 'auto']}
                            />
                            <Tooltip
                                cursor={{ stroke: Colors.purple, strokeWidth: 2 }}
                                content={({ active, payload, label }) => {
                                    if (active && payload && payload.length) {
                                        return (
                                            <GraphTooltip
                                                title={dayjs(label).format('dd MM/DD')}
                                                value={payload[0].value!.toLocaleString('en')}
                                            />
                                        );
                                    }
                                    return null;
                                }}
                            />
                            <defs>
                                <linearGradient id="splitColor" x1="0" y1="0" x2="0" y2="1">
                                    <stop stopColor={Colors.purple} stopOpacity={0.4} />
                                    <stop offset="1" stopColor={Colors.purple} stopOpacity={0.05} />
                                </linearGradient>
                            </defs>

                            <Area
                                type="monotone" dataKey="value"
                                strokeWidth={2}
                                stroke={Colors.purple} fill="url(#splitColor)" activeDot={{ r: 8 }}
                            />

                            {/* {SymphonyDefaultAnnotations({
                                startDate: selectedDate.startDate,
                                endDate: selectedDate.endDate,
                                showAnnotations,
                                setShowGridModal,
                                setSelectedCampaign,
                                setSelectedIgPost,
                                setSelectedSpotifyRelease,
                                setItemsArray
                            })} */}
                        </AreaChart>
                    </ResponsiveContainer>
                    {window.innerWidth < 768 &&
                        <div className="dateSelectorOuterContainerMobile" style={sty.dateSelectorOuterContainerMobile}>
                            <div style={{ height: 12, width: 10 }} />{/* NOTE: needed for spacing on mobile */}
                            {renderDateSelectorButtons()}
                            {renderCustomDatesPicker()}
                        </div>
                    }
                </MainChartBackground>
                :
                <div className="relative">
                    <div className="z-10 w-8/12 md:w-4/12 flex-col absolute top-1/2 left-1/2 flex items-center transform-gpu -translate-x-1/2 -translate-y-1/2 ">
                        <Loader />
                        <p className="text-sm md:text-base  text-center">We're still gathering your fanbase data. Check back tomorrow for an update.</p>
                    </div>
                    <img className={`mx-auto w-full h-40 md:h-56 opacity-70`} src={window.innerWidth < 576 ? require("assets/images/empty-state/mobile-graph-preview.png").default : require("assets/images/empty-state/desktop-graph-preview.png").default} alt="No Chart Data" />
                </div>
            }
        </div>
    )
}

const sty: Record<string, CSSProperties> = {
    dateSelectorButtonsContainer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8
    },
    dateSelectorOuterContainerMobile: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column'
    }
}

export default OverallDashboard
